import {
  IStepValueExpectations,
  TWizardStepComponentEditable,
  TStepPayload,
  TStepErrors,
  WIZARD_STUDENT_STEPS_PATHS,
  TWizardStepComponentEditableView,
  IWizardStudentState
} from "../../../interfaces";
import { Form, Input } from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { UserAPI } from "../../../../../API/userApi";
import { getChangedFields, getStepErrorsFromResponse } from "../../../utils";
import {
  StudentPastExperienceValues,
  StudentPurposeValues
} from "../../../../../interfaces/interfaces";
import { useTranslation } from "react-i18next";
import { useContextHospi } from "../../../../../context/ContextHospi";
import styled from "styled-components/macro";
import { StepViewOnlyValue } from "../../styles";
import { WizardButtonSelect } from "../../../components";

const statePath = WIZARD_STUDENT_STEPS_PATHS.Expectations;

export const WizardStepExpectations: TWizardStepComponentEditable<IWizardStudentState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IStepValueExpectations>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] =
      useState<TStepErrors<IStepValueExpectations>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);

        return UserAPI.wizardStudentSaveExpectations(values)
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);

        return UserAPI.wizardStudentSaveExpectationsPartially(
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepExpectationsView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const Root = styled(Form)``;
export const WizardStepExpectationsView: TWizardStepComponentEditableView<IStepValueExpectations> =
  React.memo(({ values, setValues, errors, readonly, viewonly }) => {
    const { currentUserRole } = useContextHospi();
    const { t } = useTranslation(currentUserRole);
    const inputSize = "large";

    const proposeOptions = StudentPurposeValues.map((v) => ({
      label: t(
        `${currentUserRole}Profile.expectations.form.propose.options.${v}`
      ),
      value: v
    }));
    const pastExperienceOptions = StudentPastExperienceValues.map((v) => ({
      label: (
        t(
          `${currentUserRole}Profile.expectations.form.pastExperience.options`,
          {
            returnObjects: true
          }
        ) as { [key: string]: string }
      )[v as string],
      value: v
    }));

    return (
      <Root layout="vertical">
        <Form.Item
          label={t(`${currentUserRole}Profile.expectations.form.propose.title`)}
          validateStatus={errors?.propose ? "error" : ""}
          help={errors?.propose}
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {proposeOptions
                .filter((c) => values.propose?.includes(c.value))
                ?.map((o) => o.label)
                .join(", ") ?? ""}
            </StepViewOnlyValue>
          ) : (
            <WizardButtonSelect
              disabled={readonly}
              value={values.propose ?? undefined}
              onChange={(newValue) =>
                setValues({ ...values, propose: newValue })
              }
              options={proposeOptions}
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(`${currentUserRole}Profile.aboutMeInfo.form.study.title`)}
          validateStatus={errors?.study ? "error" : ""}
          help={errors?.study}
        >
          {viewonly ? (
            <StepViewOnlyValue>{values.study}</StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={readonly}
              value={values.study ?? undefined}
              placeholder={t(
                `${currentUserRole}Profile.aboutMeInfo.form.study.placeholder`
              )}
              onChange={(e) => {
                setValues({
                  ...values,
                  study: e.target.value
                });
              }}
            />
          )}
        </Form.Item>
        <Form.Item
          label={t(
            `${currentUserRole}Profile.expectations.form.pastExperience.title`
          )}
          validateStatus={errors?.past_experience ? "error" : ""}
          help={errors?.past_experience}
        >
          {viewonly ? (
            <StepViewOnlyValue>
              {pastExperienceOptions
                .filter((c) => values.past_experience?.includes(c.value))
                ?.map((o) => o.label)
                .join(", ") ?? ""}
            </StepViewOnlyValue>
          ) : (
            <WizardButtonSelect
              disabled={readonly}
              value={values.past_experience ?? undefined}
              onChange={(selectedValues, newValue) => {
                if (newValue === "None of the above") {
                  setValues({
                    ...values,
                    past_experience: ["None of the above"]
                  });
                } else {
                  setValues({
                    ...values,
                    past_experience: selectedValues.filter(
                      (v) => v !== "None of the above"
                    )
                  });
                }
              }}
              options={pastExperienceOptions}
            />
          )}
        </Form.Item>
      </Root>
    );
  });
